import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';
import { sendLeadEmail } from '../services/emailService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiMail, FiUser, FiLoader } = FiIcons;

const StartScreen = () => {
  const navigate = useNavigate();
  const { userInfo, setUserInfo } = useQuiz();
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!userInfo.name.trim()) newErrors.name = 'Vorname ist erforderlich';
    if (!userInfo.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(userInfo.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSending(true);
      try {
        // Sende die E-Mail via EmailJS
        await sendLeadEmail(userInfo);
        // Nach Erfolg zum Quiz weiterleiten
        navigate('/quiz');
      } catch (error) {
        // Auch bei Fehler weiterleiten, damit der Nutzer das Quiz machen kann,
        // aber wir loggen den Fehler.
        console.error("Lead konnte nicht gesendet werden", error);
        navigate('/quiz');
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full bg-white rounded-3xl shadow-xl p-8 md:p-12 border-b-8 border-brand-red"
      >
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-6xl font-display text-brand-blue mb-6 uppercase tracking-tight">
            Der Stress-Typ-Check
          </h1>
          
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed max-w-xl mx-auto">
            <p className="font-bold text-brand-blue text-xl md:text-2xl">
              Warum reagiert dein Körper so – und was brauchst du jetzt wirklich?
            </p>
            
            <div className="h-1 w-20 bg-brand-red mx-auto my-6"></div>

            <p className="text-base text-gray-700">
              Autoimmunerkrankungen entstehen nicht nur im Körper.<br/>
              Sie verstärken sich dort, wo Menschen zu lange gegen sich selbst leben.
            </p>
            
            <p className="text-lg font-medium text-brand-blue italic">
              Mach das Quiz und du hast Klarheit.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 max-w-md mx-auto">
          <div>
            <label className="block text-sm font-bold text-brand-blue mb-2 uppercase tracking-wider">
              Dein Vorname *
            </label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-3.5 text-gray-400" />
              <input 
                type="text" 
                disabled={isSending}
                value={userInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all ${errors.name ? 'border-brand-red' : 'border-gray-200'} ${isSending ? 'bg-gray-50' : ''}`}
                placeholder="Eva"
              />
            </div>
            {errors.name && <p className="text-brand-red text-xs mt-1 font-bold">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-blue mb-2 uppercase tracking-wider">
              E-Mail Adresse *
            </label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 text-gray-400" />
              <input 
                type="email" 
                disabled={isSending}
                value={userInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all ${errors.email ? 'border-brand-red' : 'border-gray-200'} ${isSending ? 'bg-gray-50' : ''}`}
                placeholder="eva@beispiel.de"
              />
            </div>
            {errors.email && <p className="text-brand-red text-xs mt-1 font-bold">{errors.email}</p>}
          </div>

          <motion.button 
            whileHover={{ scale: isSending ? 1 : 1.02 }}
            whileTap={{ scale: isSending ? 1 : 0.98 }}
            type="submit"
            disabled={isSending}
            className={`w-full bg-brand-blue text-white py-4 px-6 rounded-xl font-display text-xl uppercase tracking-widest transition-colors duration-300 flex items-center justify-center space-x-2 shadow-lg mt-6 ${isSending ? 'opacity-70 cursor-not-allowed' : 'hover:bg-brand-red'}`}
          >
            {isSending ? (
              <>
                <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <span>Check starten</span>
                <SafeIcon icon={FiPlay} className="ml-2" />
              </>
            )}
          </motion.button>
        </form>

        <p className="text-[10px] text-gray-400 text-center mt-8 uppercase tracking-widest">
          Vertrauliche Auswertung & Datenschutz garantiert.
        </p>
      </motion.div>
    </div>
  );
};

export default StartScreen;